import {
  Banner,
  Button,
  FlexBox,
  FormField,
  FormFieldProps,
  FormGrid,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { config } from 'lib-shared-backend-config';
import { electron } from 'lib-shared-electron';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useInterval } from 'react-use';

import { HookFormSelectionList, HookFormSlider } from 'components/hookForm';
import { actions as personalActions } from 'components/settings/actions/personal';
import { electronActions } from 'middleware/electron/electron.actions';
import { articles } from 'utils/elevio';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { SettingsIntegrationsDocumentationButton } from '../../../../../integrations/components/documentationButton/documentationButton';
import { isWebHidSupportedInBrowser } from '../jabra/utils/isWebHidSupportedInBrowser';

import { busylightActions } from './busylight.actions';
import {
  busylightDeviceFilters,
  busylightDeviceManager,
  busylightRingtones,
} from './utils/busylightDeviceManager';
import { legacyBusylightMessage } from './utils/legacyBusylightMessage';

export const ConnectedDevicesBusylightView = (): JSX.Element => {
  const deviceAddedEventHandlerRef = useRef<
    ((device: HIDDevice) => void) | null
  >(null);
  const formGridColumnSpan = useBreakpointValue<FormFieldProps['span']>({
    base: 6,
    lg: 3,
  });

  const dispatch = useAppDispatch();

  const isWebHidBusylightSupported =
    !electron.isElectron() && isWebHidSupportedInBrowser();

  const { isConnectedToBusylight, busylightSettings } = useAppSelector(
    (state) => ({
      isConnectedToBusylight: state.settings.busylight.isConnectedToBusylight,
      busylightSettings: state.settings.personal.busylight,
    }),
  );

  const [isBusylightInTest, setIsBusylightInTest] = useState(false);
  const { control, watch } = useForm<FieldValues>({
    mode: 'all',
    defaultValues: {
      soundVolume: busylightSettings?.soundVolume ?? 4,
      tone: busylightSettings?.tone ? [busylightSettings?.tone] : [],
    },
  });

  const handleOnDeviceAdded = (device: HIDDevice) => {
    const isLegacyDevice = !!busylightDeviceFilters.legacyKuandoDevices.find(
      (config) =>
        config.productId === device.productId &&
        config.vendorId === device.vendorId,
    );

    if (isLegacyDevice) {
      alert(legacyBusylightMessage);
    }
  };

  useEffect(() => {
    if (!deviceAddedEventHandlerRef.current) {
      deviceAddedEventHandlerRef.current = handleOnDeviceAdded;

      busylightDeviceManager.on(
        'deviceAdded',
        deviceAddedEventHandlerRef.current,
      );
    }

    return () => {
      if (!deviceAddedEventHandlerRef.current) {
        return;
      }

      busylightDeviceManager.remove(
        'deviceAdded',
        deviceAddedEventHandlerRef.current,
      );
    };
  }, []);

  useEffect(() => {
    const subscription = watch((values) =>
      dispatch(
        personalActions.personalBusylight({
          key: 'busylight',
          value: { soundVolume: values.soundVolume, tone: values.tone[0] },
        }),
      ),
    );
    return () => subscription.unsubscribe();
  }, [watch, dispatch]);

  const checkForConnectedBusylight = useCallback(() => {
    // Clear event cache in desktop app for getConnectedStatus to succeed
    dispatch(electronActions.clearEventCache());
    dispatch(busylightActions.getConnectedStatus());
  }, [dispatch]);

  useInterval(() => {
    checkForConnectedBusylight();
  }, 5000);

  const testConnectedBusylight = () => {
    if (!isBusylightInTest) {
      setIsBusylightInTest(true);

      if (busylightSettings) {
        dispatch(busylightActions.ring({ busylight: busylightSettings }));
      }
    } else {
      setIsBusylightInTest(false);
      dispatch(busylightActions.stop());
    }

    setTimeout(() => {
      setIsBusylightInTest(false);
      dispatch(busylightActions.stop());
    }, 1000);
  };

  if (!isWebHidBusylightSupported && !electron.isElectron()) {
    return (
      <Page.Section title="Busylight">
        <FlexBox gap="200" alignItems="center">
          <Text.Paragraph>
            You need the Dixa desktop app before you can use this integration.
          </Text.Paragraph>

          <SettingsIntegrationsDocumentationButton
            onClick={() =>
              elevioClient.openArticle(articles.settings.busyLight)
            }
          />
        </FlexBox>
        <Text.ExternalLink
          target="_blank"
          rel="noreferrer"
          href={`https://${config.domain.web}/download`}
        >
          Download Dixa desktop app
        </Text.ExternalLink>
      </Page.Section>
    );
  }

  if (!electron.isElectron() && !isWebHidSupportedInBrowser()) {
    return (
      <>
        <Text.Paragraph>
          Please use a browser that supports Web USB, eg. Google Chrome or use
          the desktop app.
        </Text.Paragraph>
        <Text.ExternalLink
          target="_blank"
          rel="noreferrer"
          href={`https://${config.domain.web}/download`}
        >
          Download Dixa desktop app
        </Text.ExternalLink>
        .
      </>
    );
  }
  return (
    <>
      <Page.Section title="Connect Busylight">
        {isConnectedToBusylight ? (
          <Banner
            variant="success"
            description={
              <Text.Paragraph>A busylight is connected</Text.Paragraph>
            }
            actionSlot={
              isWebHidBusylightSupported && (
                <Button
                  variant="secondary"
                  onClick={() => busylightDeviceManager.triggerRequestDevice()}
                >
                  Request another device
                </Button>
              )
            }
          />
        ) : (
          <Banner
            variant="info"
            description={
              <Text.Paragraph>
                There is currently no connected busylight registered
              </Text.Paragraph>
            }
            actionSlot={
              isWebHidBusylightSupported && (
                <Button
                  onClick={() => busylightDeviceManager.triggerRequestDevice()}
                >
                  Request device
                </Button>
              )
            }
          />
        )}
      </Page.Section>
      <Page.Section
        title="Busylight settings"
        titleMeta={
          <SettingsIntegrationsDocumentationButton
            onClick={() =>
              elevioClient.openArticle(articles.settings.busyLight)
            }
          />
        }
      >
        <FormGrid>
          <FormField span={formGridColumnSpan}>
            <HookFormSelectionList
              name="tone"
              label="Ringtone"
              control={control}
              componentProps={{
                getItemTitleByValue: (item) => {
                  return <Text.Paragraph>{item}</Text.Paragraph>;
                },
                suffix: isConnectedToBusylight
                  ? {
                      type: 'button',
                      button: {
                        icon: isBusylightInTest ? 'stop' : 'volume-up',
                        onClick: (e) => {
                          e.stopPropagation();
                          testConnectedBusylight();
                        },
                        'aria-label': `${
                          isBusylightInTest ? 'Stop' : 'Play'
                        } busylight sound`,
                      },
                    }
                  : undefined,
              }}
            >
              {busylightRingtones.map((ringtone) => (
                <SelectionList.Item key={ringtone} value={ringtone}>
                  {ringtone}
                </SelectionList.Item>
              ))}
            </HookFormSelectionList>
          </FormField>
          <FormField span={formGridColumnSpan}>
            <HookFormSlider
              name="soundVolume"
              label="Sound volume"
              control={control}
              componentProps={{
                min: 0,
                max: 7,
              }}
            />
          </FormField>
        </FormGrid>
      </Page.Section>
    </>
  );
};
